import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Download, Upload, Trash2, Search, CheckCircle2 } from "lucide-react";

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delim = lines[0].includes(";") ? ";" : ",";
  const split = (line) => {
    const out = [];
    let cur = "", inQ = false;
    for (const ch of line) {
      if (ch === '"') inQ = !inQ;
      else if (ch === delim && !inQ) { out.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = split(lines[0]).map((h) => h.toLowerCase().replace(/[éè]/g, "e").replace(/\s+/g, "_"));
  return lines.slice(1).map((line) => {
    const cells = split(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] || ""; });
    if (row.courriel && !row.email) row.email = row.courriel;
    return row;
  });
}

export default function SeminarEnrollments() {
  const { id } = useParams();
  const [seminar, setSeminar] = useState(null);
  const [inscrits, setInscrits] = useState(null);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [importReport, setImportReport] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api(`/admin/seminaires/${id}`).then(setSeminar).catch((e) => toast.error(e.message));
    api(`/admin/seminaires/${id}/inscrits`).then(setInscrits).catch((e) => toast.error(e.message));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (search.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api(`/admin/membres?q=${encodeURIComponent(search)}&page_size=8`)
        .then((d) => setSuggestions(d.items))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const add = async (person) => {
    try {
      await api(`/admin/seminaires/${id}/inscrits`, {
        method: "POST",
        body: { person_id: person.id, acces_replay: true },
      });
      toast.success(`${person.prenom} ${person.nom} inscrit(e)`);
      setSearch("");
      setSuggestions([]);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggle = async (enr, field, value) => {
    try {
      await api(`/admin/seminaires/${id}/inscrits/${enr.id}`, { method: "PATCH", body: { [field]: value } });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const allReplay = !!inscrits?.length && inscrits.every((e) => e.acces_replay);
  const setReplayAll = async (value) => {
    const msg = value
      ? `Donner l'accès au replay aux ${inscrits.length} inscrits ?`
      : `Retirer l'accès au replay aux ${inscrits.length} inscrits ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api(`/admin/seminaires/${id}/inscrits/acces-replay`, { method: "POST", body: { acces_replay: value } });
      toast.success(`${res.modifies} inscription(s) mise(s) à jour`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (enr) => {
    if (!window.confirm(`Retirer ${enr.prenom} ${enr.nom} de ce événement ?`)) return;
    try {
      await api(`/admin/seminaires/${id}/inscrits/${enr.id}`, { method: "DELETE" });
      toast.success("Inscription retirée");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const importCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const rows = parseCsv(String(reader.result));
      if (!rows.length) { toast.error("Fichier vide ou illisible (attendu : email, prenom, nom)"); return; }
      try {
        const res = await api(`/admin/seminaires/${id}/inscrits/import`, { method: "POST", body: { rows } });
        setImportReport(res);
        toast.success(`Import terminé : ${res.inscrits} inscrit(s)`);
        load();
      } catch (e) {
        toast.error(e.message);
      }
    };
    reader.readAsText(file, "utf-8");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="seminar-enrollments-page" className="space-y-6 animate-fade-in">
        <div>
          <Link to="/admin/seminaires" data-testid="enrollments-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Tous les événements
          </Link>
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight mt-2">
            Inscrits — {seminar ? seminar.titre : "…"}
          </h1>
          {seminar && (
            <p className="text-sm text-cfhtb-text mt-1">
              {new Date(seminar.date_debut).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              {" · "}{seminar.type} · {inscrits ? `${inscrits.length} inscrit(s)` : ""}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 relative">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1.5">Ajouter un membre</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="enrollment-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nom, prénom ou email du membre…"
                  className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light"
                />
              </div>
              {suggestions.length > 0 && (
                <ul className="absolute left-4 right-4 top-full mt-1 z-40 bg-white rounded-md border border-slate-200 shadow-lg max-h-64 overflow-y-auto" data-testid="enrollment-suggestions">
                  {suggestions.map((p) => (
                    <li key={p.id}>
                      <button
                        data-testid={`enrollment-add-${p.id}`}
                        onClick={() => add(p)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm hover:bg-cfhtb-bg transition-colors text-left"
                      >
                        <span className="text-cfhtb-heading font-medium">{(p.nom || "").toUpperCase()} {p.prenom}</span>
                        <span className="text-xs text-cfhtb-text truncate">{p.email_principal}</span>
                        <UserPlus className="h-4 w-4 text-cfhtb-secondary shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="enrollments-table">
                  <thead className="bg-[#F7F7F7] border-b border-slate-200">
                    <tr>
                      {["Membre", "Source", "Référence", "Présent", "Accès replay", ""].map((h, i) => (
                        <th key={h} className={`text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3 ${i === 5 ? "text-right" : "text-left"}`}>
                          {i === 4 ? (
                            <label className="inline-flex items-center gap-2 cursor-pointer" title="Cocher / décocher pour tous les inscrits">
                              <input type="checkbox" data-testid="enrollment-replay-all" checked={allReplay}
                                disabled={!inscrits?.length} onChange={(ev) => setReplayAll(ev.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                              {h} <span className="normal-case font-normal text-cfhtb-text">(tous)</span>
                            </label>
                          ) : h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!inscrits ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                    ) : inscrits.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="enrollments-empty">Aucun inscrit pour le moment.</td></tr>
                    ) : (
                      inscrits.map((e) => (
                        <tr key={e.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors" data-testid={`enrollment-row-${e.id}`}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-cfhtb-heading">{(e.nom || "").toUpperCase()} {e.prenom}</p>
                            <p className="text-xs text-cfhtb-text">{e.email}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-cfhtb-text">{e.source}</td>
                          <td className="px-4 py-3 text-xs text-cfhtb-text">{e.reference_commande_externe || "—"}</td>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={!!e.present} data-testid={`enrollment-present-${e.id}`}
                              onChange={(ev) => toggle(e, "present", ev.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                          </td>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={!!e.acces_replay} data-testid={`enrollment-replay-${e.id}`}
                              onChange={(ev) => toggle(e, "acces_replay", ev.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button data-testid={`enrollment-remove-${e.id}`} onClick={() => remove(e)}
                              className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error transition-colors">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5">
              <h2 className="font-serif text-lg font-semibold text-cfhtb-heading mb-2">Export</h2>
              <p className="text-xs text-cfhtb-text mb-3">Liste complète des inscrits au format CSV (compatible Excel).</p>
              <a
                href={downloadUrl(`/admin/seminaires/${id}/inscrits/export.csv`)}
                data-testid="enrollments-export-csv"
                className="inline-flex items-center gap-2 w-full justify-center bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors"
              >
                <Download className="h-4 w-4" /> Exporter (CSV)
              </a>
            </div>

            <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5">
              <h2 className="font-serif text-lg font-semibold text-cfhtb-heading mb-2">Import HelloAsso</h2>
              <p className="text-xs text-cfhtb-text mb-3">
                Importez l'export CSV HelloAsso (colonnes <code className="bg-slate-100 px-1 rounded">email, prenom, nom</code>).
                Rapprochement automatique par email ; les inconnus sont créés comme prospects.
              </p>
              <button
                data-testid="enrollments-import-button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 w-full justify-center bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
              >
                <Upload className="h-4 w-4" /> Importer un CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="enrollments-import-input"
                onChange={(e) => importCsv(e.target.files?.[0])} />
              {importReport && (
                <div className="mt-4 rounded-md bg-cfhtb-success/5 border border-cfhtb-success/30 p-3 text-xs text-cfhtb-text space-y-1" data-testid="import-report">
                  <p className="font-medium text-cfhtb-heading flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-cfhtb-success" /> Résultat de l'import
                  </p>
                  <p>{importReport.inscrits} nouveau(x) inscrit(s)</p>
                  <p>{importReport.membres_crees} membre(s) créé(s)</p>
                  <p>{importReport.deja_inscrits} déjà inscrit(s) · {importReport.erreurs} ligne(s) en erreur</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
